import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    SelectChangeEvent
} from '@mui/material';
import { useSnackbar } from 'notistack';
import inventarioInformes from '../../services/inventarioInformes';

interface MovimientoStock {
    nroInterno: number;
    idformulario: number;
    codArticulo: string;
    descripcion: string;
    tipoMov: string;
    cantidad: number;
    fechaMov: Date;
    deposito: number;
    nroSuc: number;
    precioCosto: number;
    letra: string;
    nroComprobante: number;
}

interface Ubicacion {
    nroUbicacion: number;
    nombreUbicacion: string;
}

interface PaginatedResponse {
    data: MovimientoStock[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
}

// Definir los tipos de movimiento con sus variantes
const TIPOS_MOVIMIENTO = {
    '11-POS': 'AJUSTE STOCK POSITIVO',
    '11-NEG': 'AJUSTE STOCK NEGATIVO',
    '8-POS': 'TRANSFERENCIA POSITIVO',
    '8-NEG': 'TRANSFERENCIA NEGATIVO',
    '1-VEN': 'FACTURA VENTAS',
    '1-COM': 'FACTURA COMPRAS',
    '3-VEN': 'NOTA CREDITO VENTAS',
    '3-COM': 'NOTA CREDITO COMPRAS',
    '45': 'DEVOLUCION',
    '9': 'REMITO VENTAS',
    '16': 'REMITO COMPRAS',
} as const;

type TipoMovimiento = keyof typeof TIPOS_MOVIMIENTO;

const MovimientosStock: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [movimientos, setMovimientos] = useState<MovimientoStock[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
    
    // Inicializar con la fecha actual
    const today = new Date();
    const [filters, setFilters] = useState({
        fechaDesde: today.toISOString().split('T')[0],
        fechaHasta: today.toISOString().split('T')[0],
        codigoArticulo: '',
        tipoMovimiento: '',
        deposito: ''
    });

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await inventarioInformes.getMovimientosStock({
                fechaDesde: filters.fechaDesde ? new Date(filters.fechaDesde) : undefined,
                fechaHasta: filters.fechaHasta ? new Date(filters.fechaHasta) : undefined,
                codigoArticulo: filters.codigoArticulo || undefined,
                tipoMovimiento: filters.tipoMovimiento || undefined,
                deposito: filters.deposito ? parseInt(filters.deposito) : undefined,
                page: page + 1,
                pageSize: rowsPerPage
            });

            setMovimientos(response.data);
            setTotalCount(response.totalCount);
        } catch (error) {
            console.error('Error al cargar movimientos:', error);
            enqueueSnackbar('Error al cargar los movimientos de stock', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [filters, page, rowsPerPage, enqueueSnackbar]);

    // Cargar datos al montar el componente
    useEffect(() => {
        loadData();
    }, [loadData]);

    // Agregar useEffect para cargar las ubicaciones al montar el componente
    useEffect(() => {
        const loadUbicaciones = async () => {
            try {
                const data = await inventarioInformes.getUbicaciones();
                setUbicaciones(data);
            } catch (error) {
                console.error('Error al cargar ubicaciones:', error);
                enqueueSnackbar('Error al cargar las ubicaciones', { variant: 'error' });
            }
        };
        loadUbicaciones();
    }, [enqueueSnackbar]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | SelectChangeEvent<string>) => {
        const { name, value } = event.target;
        setFilters(prev => ({
            ...prev,
            [name as string]: value
        }));
    };

    const handleSearch = () => {
        setPage(0);
        loadData();
    };

    const handleExportPdf = async () => {
        try {
            setLoading(true);
            const blob = await inventarioInformes.getMovimientosStockPdf({
                fechaDesde: filters.fechaDesde ? new Date(filters.fechaDesde) : undefined,
                fechaHasta: filters.fechaHasta ? new Date(filters.fechaHasta) : undefined,
                codigoArticulo: filters.codigoArticulo || undefined,
                tipoMovimiento: filters.tipoMovimiento || undefined,
                deposito: filters.deposito ? parseInt(filters.deposito) : undefined
            });
            
            if (blob.size === 0) {
                enqueueSnackbar('No hay datos para generar el PDF', { variant: 'warning' });
                return;
            }
            
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error: any) {
            console.error('Error al generar PDF:', error);
            const errorMessage = error.response?.data?.error || 'Error al generar el PDF';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Agregar manejador específico para el cambio de ubicación
    const handleUbicacionChange = (event: SelectChangeEvent) => {
        setFilters({ ...filters, deposito: event.target.value });
    };

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                Movimientos de Stock
            </Typography>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="Fecha Desde"
                                type="date"
                                name="fechaDesde"
                                value={filters.fechaDesde}
                                onChange={handleFilterChange}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="Fecha Hasta"
                                type="date"
                                name="fechaHasta"
                                value={filters.fechaHasta}
                                onChange={handleFilterChange}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="Código Artículo"
                                name="codigoArticulo"
                                value={filters.codigoArticulo}
                                onChange={handleFilterChange}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Tipo Movimiento</InputLabel>
                                <Select
                                    name="tipoMovimiento"
                                    value={filters.tipoMovimiento}
                                    onChange={handleFilterChange}
                                    label="Tipo Movimiento"
                                >
                                    <MenuItem value="">
                                        Todos
                                    </MenuItem>
                                    <MenuItem value="11-POS">11 - AJUSTE STOCK POSITIVO</MenuItem>
                                    <MenuItem value="11-NEG">11 - AJUSTE STOCK NEGATIVO</MenuItem>
                                    <MenuItem value="8-POS">8 - TRANSFERENCIA POSITIVO</MenuItem>
                                    <MenuItem value="8-NEG">8 - TRANSFERENCIA NEGATIVO</MenuItem>
                                    <MenuItem value="1-VEN">1 - FACTURA VENTAS</MenuItem>
                                    <MenuItem value="1-COM">1 - FACTURA COMPRAS</MenuItem>
                                    <MenuItem value="3-VEN">3 - NOTA CREDITO VENTAS</MenuItem>
                                    <MenuItem value="3-COM">3 - NOTA CREDITO COMPRAS</MenuItem>
                                    <MenuItem value="45">45 - DEVOLUCION</MenuItem>
                                    <MenuItem value="9">9 - REMITO VENTAS</MenuItem>
                                    <MenuItem value="16">16 - REMITO COMPRAS</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Depósito</InputLabel>
                                <Select
                                    name="deposito"
                                    value={filters.deposito}
                                    onChange={handleUbicacionChange}
                                    label="Depósito"
                                >
                                    <MenuItem value="">
                                        Todos
                                    </MenuItem>
                                    {ubicaciones.map((ubicacion) => (
                                        <MenuItem key={ubicacion.nroUbicacion} value={ubicacion.nroUbicacion}>
                                            {ubicacion.nroUbicacion} - {ubicacion.nombreUbicacion}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Box mt={2} display="flex" gap={2}>
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={loading}
                        >
                            Buscar
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleExportPdf}
                            disabled={loading}
                        >
                            Generar PDF
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Comprobante</TableCell>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Código</TableCell>
                            <TableCell>Descripción</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Depósito</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {movimientos.map((movimiento) => (
                            <TableRow key={movimiento.nroInterno}>
                                <TableCell>
                                    {movimiento.letra && movimiento.nroSuc && movimiento.nroComprobante 
                                        ? `${movimiento.letra}-${movimiento.nroSuc.toString().padStart(4, '0')}-${movimiento.nroComprobante.toString().padStart(8, '0')}`
                                        : '-'}
                                </TableCell>
                                <TableCell>{new Date(movimiento.fechaMov).toLocaleDateString()}</TableCell>
                                <TableCell>{movimiento.codArticulo}</TableCell>
                                <TableCell>{movimiento.descripcion}</TableCell>
                                <TableCell>{movimiento.tipoMov}</TableCell>
                                <TableCell>{movimiento.deposito}</TableCell>
                                <TableCell align="right">{movimiento.cantidad}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Filas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </TableContainer>
        </Box>
    );
};

export default MovimientosStock;
